import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, Divider,
    IconButton, Alert, CircularProgress, MenuItem, InputAdornment, Snackbar,
    Chip, ListItemText
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import api from '../services/api';
import { isVisualizador } from '../services/auth';

const FORMA_LABEL = {
    PIX: 'PIX', DINHEIRO: 'Dinheiro',
    CARTAO_CREDITO: 'Crédito', CARTAO_DEBITO: 'Débito',
    BOLETO: 'Boleto', TRANSFERENCIA: 'Transferência',
};

const initialForm = {
    descricao: '', valor: '', dataVencimento: '', dataPagamento: '',
    observacoes: '', statusFinanceiro: 'PENDENTE',
    categoriaDespesa: 'OUTRO', idPlanoPagamento: '', idPsicologo: ''
};

export default function ContasPagarForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const soLeitura = isVisualizador();

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(!!id);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [planosPagamento, setPlanosPagamento] = useState([]);
    const [formData, setFormData] = useState(initialForm);

    useEffect(() => {
        api.get('/planos-pagamento?ativo=true').then(r => { const d = r.data; setPlanosPagamento(Array.isArray(d) ? d : (d?.content ?? [])); }).catch(() => {});

        if (id) {
            api.get(`/contas-pagar/${id}`)
                .then(r => {
                    const d = r.data;
                    setFormData({
                        descricao: d.descricao || '',
                        valor: d.valor || '',
                        dataVencimento: d.dataVencimento ? d.dataVencimento.substring(0, 10) : '',
                        dataPagamento: d.dataPagamento ? d.dataPagamento.substring(0, 10) : '',
                        observacoes: d.observacoes || '',
                        statusFinanceiro: d.statusFinanceiro || 'PENDENTE',
                        categoriaDespesa: d.categoriaDespesa || 'OUTRO',
                        idPlanoPagamento: d.idPlanoPagamento || '',
                        idPsicologo: d.idPsicologo || ''
                    });
                })
                .catch(() => setError('Não foi possível carregar os dados.'))
                .finally(() => setFetching(false));
        }
    }, [id]);

    const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));

    const handleSave = async () => {
        if (!formData.descricao.trim()) { setError('A descrição é obrigatória.'); return; }
        if (!formData.valor || parseFloat(formData.valor) <= 0) { setError('Informe um valor válido.'); return; }
        if (!formData.dataVencimento) { setError('A data de vencimento é obrigatória.'); return; }

        setError('');
        setLoading(true);
        try {
            const payload = {
                ...formData,
                valor: parseFloat(formData.valor),
                idPlanoPagamento: formData.idPlanoPagamento || null,
                idPsicologo: formData.idPsicologo || null,
                dataPagamento: formData.dataPagamento || null,
            };
            if (id) {
                await api.put(`/contas-pagar/${id}`, payload);
            } else {
                await api.post('/contas-pagar', payload);
            }
            setSnackbar({ open: true, message: id ? 'Despesa atualizada com sucesso!' : 'Despesa criada com sucesso!', severity: 'success' });
            setTimeout(() => navigate('/contas-pagar'), 1500);
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao salvar. Verifique a conexão com o servidor.');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/contas-pagar')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                    {soLeitura ? 'Visualizar Despesa' : id ? 'Editar Despesa' : 'Nova Despesa'}
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>

                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
                    Dados da Despesa
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            sx={{ flex: 2, minWidth: 200 }}
                            label="Descrição *"
                            value={formData.descricao}
                            onChange={set('descricao')}
                            placeholder="Ex: Aluguel do consultório"
                            disabled={soLeitura}
                        />
                        <TextField
                            sx={{ flex: 1, minWidth: 140 }}
                            label="Valor (R$) *"
                            type="number"
                            value={formData.valor}
                            onChange={set('valor')}
                            InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                            inputProps={{ min: 0, step: '0.01' }}
                            disabled={soLeitura}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField select sx={{ flex: 1, minWidth: 160 }} label="Categoria *" value={formData.categoriaDespesa} onChange={set('categoriaDespesa')} disabled={soLeitura}>
                            <MenuItem value="ALUGUEL">Aluguel</MenuItem>
                            <MenuItem value="MATERIAL">Material</MenuItem>
                            <MenuItem value="ASSINATURA_SOFTWARE">Assinatura de Software</MenuItem>
                            <MenuItem value="MARKETING">Marketing</MenuItem>
                            <MenuItem value="IMPOSTOS">Impostos</MenuItem>
                            <MenuItem value="CURSO_FORMACAO">Curso / Formação</MenuItem>
                            <MenuItem value="OUTRO">Outro</MenuItem>
                        </TextField>
                        <TextField select sx={{ flex: 1, minWidth: 160 }} label="Situação *" value={formData.statusFinanceiro} onChange={set('statusFinanceiro')} disabled={soLeitura}>
                            <MenuItem value="PENDENTE">Pendente</MenuItem>
                            <MenuItem value="PAGO">Pago</MenuItem>
                            <MenuItem value="PARCIAL">Parcial</MenuItem>
                            <MenuItem value="VENCIDO">Vencido</MenuItem>
                            <MenuItem value="CANCELADO">Cancelado</MenuItem>
                        </TextField>
                    </Box>
                </Box>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
                    Datas
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        sx={{ flex: 1, minWidth: 160 }}
                        label="Vencimento *"
                        type="date"
                        value={formData.dataVencimento}
                        onChange={set('dataVencimento')}
                        InputLabelProps={{ shrink: true }}
                        disabled={soLeitura}
                    />
                    <TextField
                        sx={{ flex: 1, minWidth: 160 }}
                        label="Data do pagamento"
                        type="date"
                        value={formData.dataPagamento}
                        onChange={set('dataPagamento')}
                        InputLabelProps={{ shrink: true }}
                        helperText="Preencha somente quando o valor for pago"
                        disabled={soLeitura}
                    />
                </Box>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
                    Plano de Pagamento
                </Typography>

                <TextField
                    select
                    fullWidth
                    label="Plano de Pagamento"
                    value={formData.idPlanoPagamento}
                    onChange={set('idPlanoPagamento')}
                    disabled={soLeitura}
                    SelectProps={{
                        renderValue: (selected) => {
                            if (!selected) return <em style={{ color: '#999' }}>Não informado</em>;
                            const p = planosPagamento.find(x => x.id_p_pagamento === selected);
                            return p ? p.nome : selected;
                        },
                        MenuProps: { PaperProps: { sx: { maxHeight: 360 } } }
                    }}
                >
                    <MenuItem value=""><em>Não informado</em></MenuItem>
                    {planosPagamento.map(p => {
                        const formas = Array.isArray(p.formasPagamento) ? p.formasPagamento : (p.formaPagamento ? [p.formaPagamento] : []);
                        return (
                            <MenuItem key={p.id_p_pagamento} value={p.id_p_pagamento} sx={{ alignItems: 'flex-start', py: 1.2 }}>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <PaymentIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.nome}</Typography>
                                            {formas.map(f => (
                                                <Chip key={f} size="small" label={FORMA_LABEL[f] || f} sx={{ height: 20, fontSize: 11 }} />
                                            ))}
                                        </Box>
                                    }
                                    secondary={
                                        p.descricaoContaBancaria && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                                <AccountBalanceIcon sx={{ fontSize: 14, color: '#888' }} />
                                                <Typography variant="caption" sx={{ color: '#666' }}>{p.descricaoContaBancaria}</Typography>
                                            </Box>
                                        )
                                    }
                                />
                            </MenuItem>
                        );
                    })}
                </TextField>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
                    Observações
                </Typography>

                <TextField
                    fullWidth multiline rows={3}
                    label="Observações"
                    value={formData.observacoes}
                    onChange={set('observacoes')}
                    placeholder="Informações adicionais sobre esta despesa..."
                    disabled={soLeitura}
                />

                <Divider sx={{ my: 4 }} />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" onClick={() => navigate('/contas-pagar')} sx={{ color: '#666', borderColor: '#ccc' }}>
                        {soLeitura ? 'Voltar' : 'Cancelar'}
                    </Button>
                    {!soLeitura && (
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            sx={{ px: 5 }}
                        >
                            {id ? 'Salvar Alterações' : 'Criar Despesa'}
                        </Button>
                    )}
                </Box>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
